import type { IssuanceProductionDataCountryMetadata } from "../useIssuanceProductionDataStore";

export enum PlIssuanceStatus {
  ProductionDataReceived = "production_data_received",
  WaitingForQuarterlyApplicationSubmission = "waiting_for_quarterly_application_submission",
  ApplicationSubmittedToTgeRegistry = "application_submitted_to_tge_registry",
  TgeRegistryOverdueSubmissionDeadline = "tge_registry_overdue_submission_deadline",
  GoosIssuedSuccessfully = "goos_issued_successfully",
}

export enum PlIssuanceFlagKey {
  ApplicationFeePaid = "application_fee_paid",
  CommunicationWithTgeInitiated = "communication_with_tge_initiated",
  AutomaticWrittenNotice = "automatic_written_notice",
  TelephoneCall = "telephone_call",
}

export interface StatusConfig {
  value: PlIssuanceStatus;
  badgeClass: string;
  labelKey: string;
  availableFlags: PlIssuanceFlagKey[];
}

export interface FlagConfig {
  key: PlIssuanceFlagKey;
  labelKey: string;
  icon: string;
  visibleForStatuses: PlIssuanceStatus[];
  showWhenFalse?: boolean;
  labelKeyWhenFalse?: string;
  childFlags?: PlIssuanceFlagKey[];
}

const APPLICATION_FEE_PILL_CLASSES = {
  paid: "bg-success-subtle text-success-emphasis",
  unpaid: "bg-warning-subtle text-warning-emphasis",
} as const;

const COMMUNICATION_PILL_CLASSES = "bg-info-subtle text-info-emphasis";

export interface PlIssuanceFlags {
  [PlIssuanceFlagKey.ApplicationFeePaid]: boolean;
  [PlIssuanceFlagKey.CommunicationWithTgeInitiated]: boolean;
  [PlIssuanceFlagKey.AutomaticWrittenNotice]: boolean;
  [PlIssuanceFlagKey.TelephoneCall]: boolean;
}

export interface PlIssuanceMetadataUpdate {
  pl_issuance_status?: PlIssuanceStatus | null;
  pl_issuance_flags?: Partial<PlIssuanceFlags>;
}

export interface PlCountryMetadata {
  pl_issuance_status?: PlIssuanceStatus | null;
  pl_issuance_flags?: Partial<PlIssuanceFlags>;
}

export const PL_STATUS_CONFIG: StatusConfig[] = [
  {
    value: PlIssuanceStatus.ProductionDataReceived,
    badgeClass: "bg-secondary",
    labelKey: "issuance_reporting.pl_status.production_data_received",
    availableFlags: [],
  },
  {
    value: PlIssuanceStatus.WaitingForQuarterlyApplicationSubmission,
    badgeClass: "bg-warning",
    labelKey:
      "issuance_reporting.pl_status.waiting_for_quarterly_application_submission",
    availableFlags: [PlIssuanceFlagKey.ApplicationFeePaid],
  },
  {
    value: PlIssuanceStatus.ApplicationSubmittedToTgeRegistry,
    badgeClass: "bg-info",
    labelKey:
      "issuance_reporting.pl_status.application_submitted_to_tge_registry",
    availableFlags: [],
  },
  {
    value: PlIssuanceStatus.TgeRegistryOverdueSubmissionDeadline,
    badgeClass: "bg-danger",
    labelKey:
      "issuance_reporting.pl_status.tge_registry_overdue_submission_deadline",
    availableFlags: [
      PlIssuanceFlagKey.CommunicationWithTgeInitiated,
      PlIssuanceFlagKey.AutomaticWrittenNotice,
      PlIssuanceFlagKey.TelephoneCall,
    ],
  },
  {
    value: PlIssuanceStatus.GoosIssuedSuccessfully,
    badgeClass: "bg-success",
    labelKey: "issuance_reporting.pl_status.goos_issued_successfully",
    availableFlags: [],
  },
];

export const PL_FLAG_CONFIG: FlagConfig[] = [
  {
    key: PlIssuanceFlagKey.ApplicationFeePaid,
    labelKey: "issuance_reporting.pl_status_flags.application_fee_paid",
    icon: "euro-sign",
    visibleForStatuses: [
      PlIssuanceStatus.WaitingForQuarterlyApplicationSubmission,
    ],
    showWhenFalse: true,
    labelKeyWhenFalse:
      "issuance_reporting.pl_status_flags.application_fee_not_paid",
  },
  {
    key: PlIssuanceFlagKey.CommunicationWithTgeInitiated,
    labelKey:
      "issuance_reporting.pl_status_flags.communication_with_tge_initiated",
    icon: "comment-dots",
    visibleForStatuses: [PlIssuanceStatus.TgeRegistryOverdueSubmissionDeadline],
    childFlags: [
      PlIssuanceFlagKey.AutomaticWrittenNotice,
      PlIssuanceFlagKey.TelephoneCall,
    ],
  },
  {
    key: PlIssuanceFlagKey.AutomaticWrittenNotice,
    labelKey: "issuance_reporting.pl_status_flags.automatic_written_notice",
    icon: "file-lines",
    visibleForStatuses: [PlIssuanceStatus.TgeRegistryOverdueSubmissionDeadline],
  },
  {
    key: PlIssuanceFlagKey.TelephoneCall,
    labelKey: "issuance_reporting.pl_status_flags.telephone_call",
    icon: "phone",
    visibleForStatuses: [PlIssuanceStatus.TgeRegistryOverdueSubmissionDeadline],
  },
];

const DEFAULT_FLAGS: PlIssuanceFlags = {
  [PlIssuanceFlagKey.ApplicationFeePaid]: false,
  [PlIssuanceFlagKey.CommunicationWithTgeInitiated]: false,
  [PlIssuanceFlagKey.AutomaticWrittenNotice]: false,
  [PlIssuanceFlagKey.TelephoneCall]: false,
};

export const getStatusConfig = (status: PlIssuanceStatus): StatusConfig => {
  const config = PL_STATUS_CONFIG.find((s) => s.value === status);
  if (!config) {
    throw new Error(`Unknown status: ${status}`);
  }
  return config;
};

export const getFlagConfig = (flag: PlIssuanceFlagKey): FlagConfig => {
  const config = PL_FLAG_CONFIG.find((f) => f.key === flag);
  if (!config) {
    throw new Error(`Unknown flag: ${flag}`);
  }
  return config;
};

export const statusToLabelKey = (status: PlIssuanceStatus): string =>
  getStatusConfig(status).labelKey;

export const flagToLabelKey = (flag: PlIssuanceFlagKey): string =>
  getFlagConfig(flag).labelKey;

export const getAvailableFlags = (
  status: PlIssuanceStatus | null
): FlagConfig[] => {
  if (!status) {
    return [];
  }
  const statusConfig = getStatusConfig(status);
  return statusConfig.availableFlags.map((flagKey) => getFlagConfig(flagKey));
};

export const STATUS_OPTIONS: PlIssuanceStatus[] = PL_STATUS_CONFIG.map(
  (s) => s.value
);

export const normalizeFlags = (
  flags?: Partial<PlIssuanceFlags> | null
): PlIssuanceFlags => ({
  ...DEFAULT_FLAGS,
  ...(flags ?? {}),
});

export const getFlagIcon = (flag: PlIssuanceFlagKey): string =>
  getFlagConfig(flag).icon;

export const getApplicationFeePillClass = (value: boolean) =>
  value
    ? APPLICATION_FEE_PILL_CLASSES.paid
    : APPLICATION_FEE_PILL_CLASSES.unpaid;

export const getCommunicationPillClass = () => COMMUNICATION_PILL_CLASSES;

export const shouldDisplayFlag = (
  flag: PlIssuanceFlagKey,
  value: boolean,
  status: PlIssuanceStatus | null
): boolean => {
  const config = getFlagConfig(flag);
  if (config.visibleForStatuses) {
    if (!status) {
      return false;
    }
    if (!config.visibleForStatuses.includes(status)) {
      return false;
    }
  }
  if (value) {
    return true;
  }
  return Boolean(config.showWhenFalse);
};

export const getFlagLabelKey = (
  flag: PlIssuanceFlagKey,
  value: boolean
): string => {
  const config = getFlagConfig(flag);
  if (!value && config.labelKeyWhenFalse) {
    return config.labelKeyWhenFalse;
  }
  return config.labelKey;
};

export const getStatusBadgeClass = (
  status: PlIssuanceStatus | null | undefined
) => {
  if (!status) {
    return "bg-secondary";
  }
  const config = PL_STATUS_CONFIG.find((s) => s.value === status);
  return config?.badgeClass ?? "bg-secondary";
};

export function getPlMetadata(
  metadata: IssuanceProductionDataCountryMetadata | undefined
): PlCountryMetadata | null {
  return (metadata?.PL as PlCountryMetadata | undefined) ?? null;
}
